import { Cassette } from '../../domain/cassette';
import { PlageHoraire } from '../../domain/plage-horaire';
import { Pompe } from '../../domain/pompe';
import { Securite } from '../../domain/securite';
import { ZoneMeasure } from '../../domain/zone-measure';
import { ZoneSamplingCondition } from '../../domain/zone-sampling-condition';
import { mapper } from '../../tools/mapping/mapper';
import { CassetteEntity, MeasureEntity, PompeEntity, PlageHoraireEntity, SecuriteEntity, ZoneSamplingConditionEntity } from '../entity';
import type { AppDB } from '../db';

export class ZoneMeasureRepository {
    constructor(private readonly database: AppDB) {}


    async getZoneMeasureEntityById(id: number) {
        return await this.database.mesures.where('Id').equals(id).first(async me => {
            return me;
        });
    }

    async getZoneMeasureEntityByCode(code: string, isDepose: boolean) {
        return await this.database.mesures.where('Code').equals(code).and(x => { return x.IsDepose === isDepose }).first(async me => {
            return me;
        });
    }

    async getZoneMeasureByIdWithChildren(id: number) {
        return await this.database.mesures.where('Id').equals(id).first(async me => {
            const zm = mapper.map(me, MeasureEntity, ZoneMeasure);
            if (me?.CassetteId) {
                zm.Cassette = await this.database.getCassetteByIdWithChildren(me.CassetteId);
            }
            if (me?.SamplingConditionId) {
                const sce = await this.database.getSamplingConditionEntityById(me.SamplingConditionId);
                const sc = mapper.map(sce, ZoneSamplingConditionEntity, ZoneSamplingCondition)
                zm.ZoneSamplingCondition = sc;
            }
            if (me?.PoseSecuriteId) {
                const se = await this.database.getSecuriteEntityById(me.PoseSecuriteId);
                const s = mapper.map(se, SecuriteEntity, Securite)
                zm.PoseSecurite = s;
            }
            if (me?.DePoseSecuriteId) {
                const se = await this.database.getSecuriteEntityById(me.DePoseSecuriteId);
                const s = mapper.map(se, SecuriteEntity, Securite)
                zm.DeposeSecurite = s;
            }
            return zm;
        });
    }

    async getZoneMeasureByCodeWithChildren(code: string, isDepose: boolean) {
        return await this.database.mesures.where('Code').equals(code).and(x => { return x.IsDepose === isDepose }).first(async me => {
            const zm = mapper.map(me, MeasureEntity, ZoneMeasure);
            if (me?.CassetteId) {
                zm.Cassette = await this.database.getCassetteByIdWithChildren(me.CassetteId);
            }
            if (me?.SamplingConditionId) {
                const sce = await this.database.getSamplingConditionEntityById(me.SamplingConditionId);
                const sc = mapper.map(sce, ZoneSamplingConditionEntity, ZoneSamplingCondition)
                zm.ZoneSamplingCondition = sc;
            }
            if (me?.PoseSecuriteId) {
                const se = await this.database.getSecuriteEntityById(me.PoseSecuriteId);
                const s = mapper.map(se, SecuriteEntity, Securite)
                zm.PoseSecurite = s;

            }
            if (me?.DePoseSecuriteId) {
                const se = await this.database.getSecuriteEntityById(me.DePoseSecuriteId);
                const s = mapper.map(se, SecuriteEntity, Securite)
                zm.DeposeSecurite = s;
            }
            return zm;
        });
    }

    async attachMeasure(measure: ZoneMeasure, zoneId: number) {
        let zoneMeasureId: number;
        const zme = mapper.map(measure, ZoneMeasure, MeasureEntity);

        const ze = await this.database.getZoneEntityById(zoneId);
        zoneMeasureId = await this.database.mesures.add(zme);
        await this.database.zones.update(zoneId, {
            MeasureIds: ze?.MeasureIds ? [...ze.MeasureIds, zoneMeasureId] : [zoneMeasureId]
        });

        if (measure.Cassette) {
            this.database.attachCassette(measure.Cassette, zoneMeasureId);
        }

        if (measure.PoseSecurite) {
            this.database.attachSecuriteToZone(measure.PoseSecurite, true, zoneMeasureId);
        }
        if (measure.DeposeSecurite) {
            this.database.attachSecuriteToZone(measure.DeposeSecurite, false, zoneMeasureId);
        }

        if (measure.ZoneSamplingCondition) {
            this.database.attachSamplingCondition(measure.ZoneSamplingCondition, zoneMeasureId);
        }
        return zoneMeasureId;

    }

    async updateMeasure(measure: ZoneMeasure) {

        let zme = await this.database.getZoneMeasureEntityByCode(measure.Code, measure.IsDepose);
        if (!zme || !zme.Id) {
            return false;
        }

        if (zme.CassetteId) {
            const ce = await this.database.getCassetteEntityById(zme.CassetteId);
            if (!ce || !ce.Id) {
                return false;
            }

            if (ce.PompeId) {
                const sce = await this.database.getPompeEntityById(ce.PompeId);
                if (!sce) {
                    return false;
                }
                mapper.mutate(measure.Cassette.Pompe, sce, Pompe, PompeEntity);
                await this.database.pompes.update(ce.PompeId, sce);
            }
            if (measure.Cassette.PlageHoraireList?.length > 0) {
                const newPhArray: number[] = [];
                if (ce.PlageHoraireIds) {
                    await this.database.clearPlageHoraireById(ce.PlageHoraireIds)
                    await this.database.cassettes.update(ce.Id, { PlageHoraireIds: [] })
                }
                for (let i = 0; i < measure.Cassette.PlageHoraireList.length; i++) {
                    const phe = mapper.map(measure.Cassette.PlageHoraireList[i], PlageHoraire, PlageHoraireEntity);
                    const phId = await this.database.plageHoraires.add(phe);
                    newPhArray.push(phId);
                }
                ce.PlageHoraireIds = newPhArray
            }
            mapper.mutate(measure.Cassette, ce, Cassette, CassetteEntity);
            await this.database.cassettes.update(zme.CassetteId, ce);
        }

        if (zme.SamplingConditionId) {
            const sce = await this.database.getSamplingConditionEntityById(zme.SamplingConditionId);
            if (!sce) {
                return false;
            }
            mapper.mutate(measure.ZoneSamplingCondition, sce, ZoneSamplingCondition, ZoneSamplingConditionEntity);
            await this.database.zoneSamplingConditions.update(zme.SamplingConditionId, sce);
        }

        if (zme.DePoseSecuriteId) {
            const dse = await this.database.getSecuriteEntityById(zme.DePoseSecuriteId);
            if (!dse) {
                return false;
            }
            mapper.mutate(measure.DeposeSecurite, dse, Securite, SecuriteEntity);
            await this.database.securites.update(zme.DePoseSecuriteId, dse);
        }

        if (zme.PoseSecuriteId) {
            const pse = await this.database.getSecuriteEntityById(zme.PoseSecuriteId);
            if (!pse) {
                return false;
            }
            mapper.mutate(measure.PoseSecurite, pse, Securite, SecuriteEntity);
            await this.database.securites.update(zme.PoseSecuriteId, pse);
        }
        mapper.mutate(measure, zme, ZoneMeasure, MeasureEntity);
        await this.database.mesures.update(zme.Id, zme);

        return true;
    }

    async clearZoneMeasureWithChildren(measure: ZoneMeasure) {
        const zme = await this.database.getZoneMeasureEntityByCode(measure.Code, measure.IsDepose);
        if (!zme || !zme.SamplingConditionId || !zme.CassetteId) {
            return;
        }

        const ce = await this.database.getCassetteEntityById(zme.CassetteId)
        for (let k = 0; ce?.PlageHoraireIds && k < ce.PlageHoraireIds.length; k++) {
            const phe = await this.database.getPlageHoraireEntityById(ce?.PlageHoraireIds[k]!)
            await this.database.plageHoraires.where('Id').equals(phe?.Id!).delete();
        }
        await this.database.cassettes.where('Id').equals(ce?.Id!).delete();
        await this.database.cassettes.update(ce!, { PlageHoraireIds: [] });

        await this.database.zoneSamplingConditions.where('Id').equals(zme.SamplingConditionId).delete();
    }

    async cancelledZoneMeasure(measure: ZoneMeasure, reason: string) {
        const zme = await this.database.getZoneMeasureEntityByCode(measure.Code, measure.IsDepose);
        if (!zme || !zme.Id) {
            return;
        }

        await this.database.mesures.update(zme.Id, { IsCancelled: true, Observation: reason });
        return true;
    }
}
